import { useMemo, ReactNode } from "react";
import { Link } from "react-router-dom";
import { useFetcherData } from "v2/hooks/useFetcherData";
import { FetcherKeys } from "routes/dataroutes/Fetchers";
import { BillingPath } from "v2/pages/billing/BillingPath";
import { usePaymentSources } from "v2/pages/billing/hooks/usePaymentSources";
import { checkValidity } from "utils/validationUtils";
import { PaymentSource } from "types/billing/PaymentSource";


/** Response from /billing-data/billing-info loader */
interface BillingInfoResponse {
  /** Fields like first_name, last_name, city, country, etc. Each wrapped as { value, validation } */
  billingAddress: Record<string, any>;
  paymentSources: PaymentSource[];
  /** Chargebee VirtualBankAccount — contains id, bank_name, account_number, routing_number, etc. */
  virtualBankAccount?: Record<string, any>;
}


export type BillingReadinessReason = "missing-address" | "missing-payment" | "missing-both";

export interface BillingReadiness {
  /** True only once address + payment are confirmed present (false while loading / on missing). */
  isReady: boolean;
  /** True while the address/payment data is still resolving (vs. resolved-incomplete). */
  isLoading: boolean;
  reason?: BillingReadinessReason;
  /** Tooltip (message + Billing Details link) when something's missing; null otherwise. */
  tooltip: ReactNode;
}

const DETAILS_PATH = `${BillingPath}/details`;

const READINESS_MESSAGE: Record<BillingReadinessReason, { message: string, anchor?: string }> = {
  "missing-address": {
    message: "Add a billing address before changing your plan.",
    anchor: "#billing-address"
  },
  "missing-payment": {
    message: "Add a payment method before changing your plan.",
    anchor: "#payment-method"
  },
  "missing-both": {
    message: "Add a billing address and payment method before changing your plan."
  },
};


export default function useBillingInfo() {
  const { value, reload, fetcher, error: billingInfoError } = useFetcherData<BillingInfoResponse>({
    fetcherKey: FetcherKeys.BILLING_DETAILS,
    fetcherHref: "/billing-data/billing-info",
  });

  const isValidBillingAddress = useMemo(() => {
    const billingAddress = value?.billingAddress;

    if (!billingAddress) {
      return undefined;
    }

    if (Object.keys(billingAddress).length === 0) {
      // empty billing address
      return false;
    }

    for (const field in billingAddress) {
      if (!checkValidity(billingAddress[field].value, billingAddress[field].validation)) {
        return false;
      }
    }

    return true;
  }, [ value?.billingAddress ]);

  const hasValidPaymentSource = useMemo(() => {
    const paymentSources = value?.paymentSources;

    if (paymentSources === undefined) {
      return undefined;
    }

    return paymentSources.length > 0;
  }, [ value?.paymentSources ]);


  // From the payment-sources fetcher (kept fresh by card actions); the billing-info loader's
  // own paymentSources can go stale after a card add/remove.
  const { paymentSources: livePaymentSources, paymentSourcesError } = usePaymentSources();
  const hasPaymentMethod = livePaymentSources === undefined ? undefined : livePaymentSources.length > 0;

  // A VBAN exists (with or without card/PayPal backups). For wire-transfer framing.
  const hasWireTransferOnAccount = Boolean(value?.virtualBankAccount?.id);

  // VBAN exists AND no card/PayPal on file — wire transfer is the only way to pay.
  const isWireTransferOnly = useMemo(() => {
    const currentPaymentSources = livePaymentSources ?? value?.paymentSources;
    return hasWireTransferOnAccount
      && currentPaymentSources !== undefined
      && currentPaymentSources.length === 0;
  }, [ hasWireTransferOnAccount, value?.paymentSources, livePaymentSources ]);

  // Gates paid plan changes on complete billing details. Disabled while loading; on a fetch
  // error we can't tell, so allow the attempt (the backend redirect is the authoritative gate).
  const billingReadiness: BillingReadiness = useMemo(() => {
    const isLoading =
      (isValidBillingAddress === undefined && !billingInfoError)
      || (hasPaymentMethod === undefined && !paymentSourcesError);
    const addressMissing = isValidBillingAddress === false;
    const paymentMissing = hasPaymentMethod === false && !isWireTransferOnly;

    const reason: BillingReadinessReason | undefined =
      addressMissing && paymentMissing ? "missing-both"
      : addressMissing ? "missing-address"
      : paymentMissing ? "missing-payment"
      : undefined;

    return {
      isReady: !isLoading && reason === undefined,
      isLoading,
      reason,
      tooltip: reason
        ? (
          <span>
            {READINESS_MESSAGE[reason]?.message}{" "}
            <Link to={ DETAILS_PATH + (READINESS_MESSAGE[reason]?.anchor ?? "") } className="underline">
              Go to Billing Details
            </Link>
          </span>
        )
        : null,
    };
  }, [ isValidBillingAddress, hasPaymentMethod, isWireTransferOnly, billingInfoError, paymentSourcesError ]);

  const isBillingInfoLoaded = fetcher?.state === "idle" && fetcher?.data !== undefined && billingInfoError === undefined;

  return {
    billingAddress: {
      data: value?.billingAddress,
      isValid: isValidBillingAddress,
    },
    paymentSources: {
      data: value?.paymentSources,
      hasValidPaymentSource
    },
    virtualBankAccount: value?.virtualBankAccount,
    // Settled successfully — trust VBAN status only when true.
    isBillingInfoLoaded,
    // In flight (false on success AND error) — gate blocking on this so an error doesn't wait forever.
    isBillingInfoLoading: !isBillingInfoLoaded && billingInfoError === undefined,
    hasWireTransferOnAccount,
    isWireTransferOnly,
    billingReadiness,
    reload,
    fetcher,
  };
};
